import React, { useState } from 'react'
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded'
import copy from 'copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import {
  NATIONAL_DAY_CASHBACK_CAP,
  NATIONAL_DAY_CASHBACK_RATE,
  NATIONAL_DAY_COLORS,
  NATIONAL_DAY_COUPON,
  NATIONAL_DAY_LOGO,
  checkerboard
} from './nationalDay'

export default function NationalDayOffer() {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const copyCouponHandler = () => {
    copy(NATIONAL_DAY_COUPON)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        p: 2.25,
        pt: 2.5,
        borderRadius: 2,
        border: `1px solid ${alpha(NATIONAL_DAY_COLORS.green, 0.35)}`,
        background: `linear-gradient(135deg, #F4FAF6 0%, #FFFFFF 62%)`
      }}
    >
      {/* Checkerboard rule across the top edge — the lockup's signature motif */}
      <Box
        sx={{
          position: 'absolute',
          insetInline: 0,
          top: 0,
          height: 6,
          ...checkerboard(6),
          pointerEvents: 'none'
        }}
      />

      {/* Checkerboard watermark, faded to a circle so it reads the same in both directions.
          Kept in the bottom corner so it never sits behind the lockup. */}
      <Box
        sx={{
          position: 'absolute',
          insetInlineEnd: -24,
          bottom: -24,
          width: { xs: 120, md: 160 },
          height: { xs: 120, md: 160 },
          opacity: 0.08,
          ...checkerboard(16),
          maskImage: 'radial-gradient(circle, #000 0%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(circle, #000 0%, transparent 72%)',
          pointerEvents: 'none'
        }}
      />

      {/* Soft green glow on the opposite corner */}
      <Box
        sx={{
          position: 'absolute',
          insetInlineStart: -46,
          top: -54,
          width: 160,
          height: 160,
          borderRadius: '50%',
          bgcolor: alpha(NATIONAL_DAY_COLORS.green, 0.09),
          pointerEvents: 'none'
        }}
      />

      <Stack spacing={1.5} sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={NATIONAL_DAY_LOGO}
          alt={t('nationalDayOffer.badge')}
          sx={{
            height: { xs: 32, md: 40 },
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
            alignSelf: 'flex-start'
          }}
        />

        <Stack spacing={0.5}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, lineHeight: 1.25, color: NATIONAL_DAY_COLORS.deep }}
          >
            {t('nationalDayOffer.title', { rate: NATIONAL_DAY_CASHBACK_RATE })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('nationalDayOffer.description', {
              rate: NATIONAL_DAY_CASHBACK_RATE,
              limit: t('MoneyAmount', { amount: NATIONAL_DAY_CASHBACK_CAP })
            })}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          useFlexGap
          flexWrap="wrap"
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            border: `1.5px dashed ${alpha(NATIONAL_DAY_COLORS.green, 0.55)}`,
            bgcolor: alpha(NATIONAL_DAY_COLORS.green, 0.06)
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <RedeemRoundedIcon sx={{ fontSize: 22, color: NATIONAL_DAY_COLORS.green }} />
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                {t('nationalDayOffer.couponLabel')}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, md: 20 },
                  letterSpacing: 3,
                  lineHeight: 1.3,
                  color: NATIONAL_DAY_COLORS.deep,
                  direction: 'ltr'
                }}
              >
                {NATIONAL_DAY_COUPON}
              </Typography>
            </Stack>
            <Tooltip title={t(copied ? 'Copied' : 'Copy')} placement="top">
              <IconButton
                size="small"
                onClick={copyCouponHandler}
                aria-label={t('Copy')}
                sx={{
                  color: copied ? NATIONAL_DAY_COLORS.green : NATIONAL_DAY_COLORS.deep,
                  bgcolor: alpha(NATIONAL_DAY_COLORS.green, 0.1),
                  '&:hover': { bgcolor: alpha(NATIONAL_DAY_COLORS.green, 0.2) }
                }}
              >
                {copied
                  ? <CheckRoundedIcon sx={{ fontSize: 18 }} />
                  : <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={0.25} alignItems="center">
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1,
                color: NATIONAL_DAY_COLORS.green
              }}
            >
              {t('nationalDayOffer.rate', { rate: NATIONAL_DAY_CASHBACK_RATE })}
            </Typography>
            <Typography
              variant="caption"
              sx={{ lineHeight: 1.2, textAlign: 'center', color: NATIONAL_DAY_COLORS.deep, opacity: 0.75 }}
            >
              {t('nationalDayOffer.cap', {
                limit: t('MoneyAmount', { amount: NATIONAL_DAY_CASHBACK_CAP })
              })}
            </Typography>
          </Stack>
        </Stack>

        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: NATIONAL_DAY_COLORS.green }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: NATIONAL_DAY_COLORS.deep }}>
              {t('nationalDayOffer.validity')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: NATIONAL_DAY_COLORS.green }} />
            <Typography variant="caption" color="text.secondary">
              {t('nationalDayOffer.footer')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}
